import { useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { buildCanonicalUrl } from '../lib/routeSeo';
import { normalizeUiLanguageCode } from '../constants/uiLanguages';
import useNoindexMeta from '../hooks/useNoindexMeta';

const SITE_NAME = 'Aprendos';
const DEFAULT_IMAGE = 'https://aprendos.com/og-image.png';
const OG_LOCALES = { en: 'en_US', es: 'es_ES', no: 'nb_NO' };

export function getOgLocale(languageCode) {
  return OG_LOCALES[normalizeUiLanguageCode(languageCode)] || OG_LOCALES.en;
}

// `noindex` is for valid pages that should not be in search results: utility steps
// (language chooser, booking flow) and sign-in-gated episode pages, which a crawler
// only ever sees as an empty shell.
export default function PageMeta({ title, description, ogImage, type = 'website', canonicalUrl: canonicalUrlOverride, noindex = false, titleIncludesSiteName = false }) {
  const location = useLocation();
  const { i18n } = useTranslation();
  const canonicalUrl = canonicalUrlOverride || buildCanonicalUrl(location.pathname);
  const fullTitle = titleIncludesSiteName ? title : `${title} | ${SITE_NAME}`;
  const image = ogImage || DEFAULT_IMAGE;
  const ogLocale = getOgLocale(i18n.resolvedLanguage || i18n.language);

  useEffect(() => {
    const upsertMeta = (attribute, name, content) => {
      const selector = `meta[${attribute}="${name}"]`;
      const elements = Array.from(document.head.querySelectorAll(selector));
      let element = elements[0];
      if (!element) {
        element = document.createElement('meta');
        element.setAttribute(attribute, name);
        document.head.appendChild(element);
      }
      element.setAttribute('content', content);
      elements.slice(1).forEach((duplicate) => duplicate.remove());
    };

    document.title = fullTitle;

    const canonicalLinks = Array.from(document.head.querySelectorAll('link[rel="canonical"]'));
    let canonicalLink = canonicalLinks[0];
    if (!canonicalLink) {
      canonicalLink = document.createElement('link');
      canonicalLink.setAttribute('rel', 'canonical');
      document.head.appendChild(canonicalLink);
    }
    canonicalLink.setAttribute('href', canonicalUrl);
    canonicalLinks.slice(1).forEach((duplicate) => duplicate.remove());

    upsertMeta('name', 'description', description);
    upsertMeta('property', 'og:title', fullTitle);
    upsertMeta('property', 'og:description', description);
    upsertMeta('property', 'og:url', canonicalUrl);
    upsertMeta('property', 'og:image', image);
    upsertMeta('property', 'og:type', type);
    upsertMeta('name', 'twitter:title', fullTitle);
    upsertMeta('name', 'twitter:description', description);
    upsertMeta('name', 'twitter:image', image);
    upsertMeta('property', 'og:locale', ogLocale);

    document.head.querySelectorAll('meta[property="og:locale:alternate"]').forEach((node) => node.remove());
    Object.values(OG_LOCALES)
      .filter((locale) => locale !== ogLocale)
      .forEach((locale) => {
        const alternate = document.createElement('meta');
        alternate.setAttribute('property', 'og:locale:alternate');
        alternate.setAttribute('content', locale);
        document.head.appendChild(alternate);
      });
  }, [canonicalUrl, description, fullTitle, image, ogLocale, type]);

  useNoindexMeta(noindex);

  return null;
}
